import axiosInstance from '../helpers/axiosInstance'

const PLATFORM_URL = process.env.REACT_APP_PLATFORM_URL

/**
 * POST /api/v1/shipping-coverages/bulk-create
 *
 * payload shape:
 * {
 *   shipping_method_id: number,
 *   dry_run: boolean,
 *   coverages: [{ value, zone, extended_zone?, po_box_dropoff?, delivery_sla_in_hours?, delivery_sla_offset?, eta_offset?, content_value_fee?, fee_offset? }],
 *   prerequisites?: {
 *     xdock_zone_bindings?: [{ xdock_id, zone_names: [] }],
 *     shipping_costs?:      [{ tarif_zone, cost, max_compound_weight?, currency_code? }]
 *   }
 * }
 *
 * Returns the full response body (status 200 dry-run / 202 commit / 422 validation failed)
 * — caller decides what to render based on response.error_code or response.job_id.
 */
export const bulkCreateShippingCoverages = async payload => {
  try {
    const res = await axiosInstance.post(`${PLATFORM_URL}/api/v1/shipping-coverages/bulk-create`, payload)
    return res.data
  } catch (err) {
    if (err.response && err.response.status === 422) {
      // Validation failure — return the structured error body to the caller.
      return err.response.data
    }
    throw err
  }
}

/**
 * GET /api/v1/lastmile/xdocks?country=MX&include=zones
 *
 * Returns: [{ id, name, country, config, zone_names? }]
 */
export const fetchLastmileXdocks = async ({ country, includeZones = false } = {}) => {
  const params = {}
  if (country) params.country = country
  if (includeZones) params.include = 'zones'
  const res = await axiosInstance.get(`${PLATFORM_URL}/api/v1/lastmile/xdocks`, { params })
  return res.data
}

/**
 * POST /api/v1/lastmile/xdocks/:id/zones
 *
 * Returns: { xdock_id, created_zone_names, already_present_zone_names }
 */
export const createXdockZones = async (xdockId, zoneNames) => {
  const res = await axiosInstance.post(`${PLATFORM_URL}/api/v1/lastmile/xdocks/${xdockId}/zones`, { zone_names: zoneNames })
  return res.data
}

/**
 * POST /api/v1/lastmile/xdocks — create a cross-dock.
 * data: { name, shipping_address, shipping_zipcode, country ("MX"|"BR"),
 *         phone?, shipping_number? }
 */
export const createXdock = async data => {
  const res = await axiosInstance.post(`${PLATFORM_URL}/api/v1/lastmile/xdocks`, data)
  return res.data
}

/**
 * PATCH /api/v1/lastmile/xdocks/:id — update a cross-dock (all fields optional).
 */
export const updateXdock = async (id, data) => {
  const res = await axiosInstance.patch(`${PLATFORM_URL}/api/v1/lastmile/xdocks/${id}`, data)
  return res.data
}

/**
 * GET /api/v1/shipping-methods/:id/shipping-costs
 *
 * Returns: { shipping_method_id, country, shipping_costs: [{ id, tarif_zone, cost, max_compound_weight, currency_code }] }
 */
export const fetchShippingCostsForMethod = async shippingMethodId => {
  const res = await axiosInstance.get(`${PLATFORM_URL}/api/v1/shipping-methods/${shippingMethodId}/shipping-costs`)
  return res.data
}

/**
 * POST /api/v1/shipping-methods/:id/shipping-costs
 *
 * Returns: { id, shipping_method_id, tarif_zone, cost, max_compound_weight, currency_code }
 */
export const createShippingCost = async (shippingMethodId, { tarif_zone, cost, max_compound_weight, currency_code }) => {
  const res = await axiosInstance.post(`${PLATFORM_URL}/api/v1/shipping-methods/${shippingMethodId}/shipping-costs`, {
    tarif_zone,
    cost,
    max_compound_weight,
    currency_code,
  })
  return res.data
}
